import convert from "color-convert";
import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";
import { SketchPicker, type ColorResult } from "react-color";

import { PRESET_COLORS } from "$/lib/utils/constants/colors.constants";
import { cn } from "$/lib/utils/functions/misc.functions";
import {
  addNewComponent,
  createSpaceForComponent,
  searchForElement,
} from "$/lib/utils/functions/util.functions";
import type { PresetColor } from "react-color/lib/components/sketch/Sketch";
import useEyeDropper from "use-eye-dropper";
import { AddColor } from "./AddColor";
import "./ColorPicker.styles.scss";
import type { ColorPickerProps } from "./ColorPicker.types";
import { EyeDropPicker } from "./EyeDropPicker";

const ColorPicker = forwardRef<
  HTMLDivElement,
  PropsWithChildren<ColorPickerProps>
>(
  (
    { containerClassName, onChange, color, ...sketchPickerProps },
    forwardedRef,
  ) => {
    const ref = useRef<SketchPicker | null>(null);

    const [currentHex, setCurrentHex] = useState<string>();

    const [savedColors, setSavedColors] =
      useState<PresetColor[]>(PRESET_COLORS);

    const { open, isSupported } = useEyeDropper();

    const handleOnChange = (color: ColorResult) => {
      const hexColor = color.hex;

      if (onChange) onChange(color);

      const elem = document.querySelector(
        `div[title="${hexColor.toUpperCase()}"]`,
      );

      let prevElem: HTMLDivElement | null = null;

      if (!elem) return;

      if (currentHex)
        prevElem = document.querySelector(`div[title="${currentHex}"]`);

      if (prevElem) {
        prevElem.style.boxShadow = "rgba(0, 0, 0, 0.15) 0px 0px 0px 1px inset";
        //@ts-expect-error native type
        prevElem.parentNode?.parentNode?.classList.remove("presetButton");
      }

      //@ts-expect-error native type
      elem.style.boxShadow = "none";
      //@ts-expect-error native type
      elem.parentNode?.parentNode?.classList.add("presetButton");

      setCurrentHex(hexColor.toUpperCase());
    };

    const pickColor = useCallback(() => {
      const openPicker = async () => {
        try {
          const color = await open();
          const rgba = convert.hex.rgb(color.sRGBHex);
          const hsl = convert.hex.hsl(color.sRGBHex);

          handleOnChange({
            hex: color.sRGBHex,
            rgb: { r: rgba[0], g: rgba[1], b: rgba[2] },
            hsl: { h: hsl[0], s: hsl[1], l: hsl[2] },
          });
        } catch (e) {
          console.log(e);
        }
      };
      openPicker();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleSaveColor = useCallback(
      (color: PresetColor) => {
        if (savedColors.length >= 21) savedColors.pop();

        setSavedColors((prev) => [color, ...prev]);
      },
      [savedColors],
    );

    const addEyeDropPickerComponent = useCallback(() => {
      const container = searchForElement(".flexbox-fix", 1);

      if (!container || !isSupported) return;

      const newClassName = "eye-drop-picker-container";

      let newDiv = container.querySelector(`.${newClassName}`);

      if (!newDiv) {
        newDiv = createSpaceForComponent(newClassName);
        container.appendChild(newDiv);
      }

      addNewComponent(
        <EyeDropPicker isSupported={isSupported()} pickColor={pickColor} />,
        newDiv,
      );
    }, [isSupported, pickColor]);

    const addRecentColorSection = useCallback(() => {
      const container = searchForElement(".sketch-picker", 0);

      if (!container) return;

      const newClassName = "save-color-button";

      let newDiv = container.querySelector(`.${newClassName}`);

      if (!newDiv) {
        newDiv = createSpaceForComponent(newClassName);
        container.insertBefore(newDiv, container.lastElementChild);
      }

      const hex = `#${convert.rgb.hex(color.r, color.g, color.b)}${color.a && color.a !== 1 ? 100 * color.a : ""}`;

      addNewComponent(
        <AddColor onSave={() => handleSaveColor({ color: hex, title: hex })} />,
        newDiv,
      );
    }, [color, handleSaveColor]);

    useEffect(() => {
      addEyeDropPickerComponent();
    }, [addEyeDropPickerComponent]);

    useEffect(() => {
      addRecentColorSection();
    }, [addRecentColorSection]);

    return (
      <div ref={forwardedRef} className={cn(containerClassName)}>
        <SketchPicker
          ref={ref}
          className="sketch-picker"
          {...sketchPickerProps}
          color={color}
          onChange={handleOnChange}
          presetColors={savedColors}
        />
      </div>
    );
  },
);

ColorPicker.displayName = "ColorPicker";

export default ColorPicker;
